import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SlidersVertical, ArrowRight, Ear, Layers3, Loader2, Mic2, Repeat2, Trash2 } from "lucide-react";
import VersionPlayer from "@/components/creator/music/VersionPlayer";
import LyricsCheckPanel from "@/components/creator/music/LyricsCheckPanel";
import OpenInStudioButton from "@/components/creator/music/OpenInStudioButton";
import { checkVersionLyrics, fmtClock, shortVoiceName, sourceAudioUrl } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const TUNE_LABEL = { natural: "Tuned", tight: "Tight-tuned" };
const AFTER_LABEL = { add_band: "With the band", add_singing: "With singing", grow_loop: "Full song" };

/** The Before/After switch only when this version was built on the upload that's still on the project. */
const beforeAfter = (projectId, version, source) => (AFTER_LABEL[version.start_mode] && source?.uploaded_at && source.uploaded_at === version.source_uploaded_at
  ? { url: sourceAudioUrl(projectId, source), beforeLabel: version.start_mode === "grow_loop" ? "My loop" : "My upload", afterLabel: AFTER_LABEL[version.start_mode] } : null);

/** One generated (or re-sung) take inside a project. */
export default function VersionCard({ projectId, version, hasLyrics, onChanged, onRemove, onRegenerate, source, lyrics }) {
  const [rechecking, setRechecking] = useState(false);
  const myVoice = version.kind === "my_voice";
  const myVocal = version.kind === "my_vocal";
  const singHref = version.kind === "generated" ? `/create/project/${projectId}/sing?version=${version.id}` : null;
  const replaceHref = version.kind === "generated" ? `/create/project/${projectId}/replace?version=${version.id}`
    : myVocal && version.derived_from ? `/create/project/${projectId}/replace?version=${version.derived_from}${version.take_id ? `&take=${version.take_id}` : ""}` : null;
  const touch = version.touchups;
  const kindLabel = myVoice ? `My Recorded Voice · ${version.voice_name || "voice"}` : myVocal ? `My vocal · ${shortVoiceName(version.voice_name) || "me"}` : `Version ${version.take}`;

  const recheck = async () => {
    setRechecking(true);
    try {
      await checkVersionLyrics(projectId, version.id);
      await onChanged();
    } catch (e) {
      toast.error("Couldn't check the lyrics", { description: safeApiError(e) });
    } finally {
      setRechecking(false);
    }
  };

  return (
    <article data-testid={`version-${version.id}`} data-kind={version.kind} className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
            <span data-testid={`version-kind-${version.id}`} className={myVoice ? "text-amber-200" : myVocal ? "text-rose-200" : "text-blue-300/90"}>{kindLabel}</span>
            {myVoice ? <span className="rounded-full border border-amber-300/40 px-1.5 py-px text-[9px] text-amber-200">Experimental</span> : null}
            {myVocal ? <span className="rounded-full border border-rose-300/40 px-1.5 py-px text-[9px] text-rose-200">Sung live</span> : null}
            {touch?.tune && touch.tune !== "off" ? <span data-testid={`version-tuned-${version.id}`} className="rounded-full border border-amber-300/40 px-1.5 py-px text-[9px] text-amber-200">{TUNE_LABEL[touch.tune]}</span> : null}
            {touch?.denoise ? <span data-testid={`version-cleaned-${version.id}`} className="rounded-full border border-sky-300/40 px-1.5 py-px text-[9px] text-sky-200">Noise cleaned</span> : null}
            {version.duration ? <span>{fmtClock(version.duration)}</span> : null}
            {version.vocal_take ? <span className="text-emerald-300">Your vocal take ✓</span> : null}
            {version.master?.label ? <span className="text-emerald-300">Mastered · {version.master.label}</span> : null}
          </p>
          <h3 data-testid={`version-title-${version.id}`} className="mt-1 truncate font-display text-lg font-semibold">{version.title}</h3>
          {version.note ? <p className="mt-1 text-[11px] text-white/45">{version.note}</p> : null}
        </div>
        <button type="button" data-testid={`version-remove-${version.id}`} onClick={() => onRemove(version)} aria-label="Remove version" className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 text-white/45 hover:text-rose-300">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <VersionPlayer id={version.id} url={version.audio_url} duration={version.duration} alt={beforeAfter(projectId, version, source)} />
      <div className="mt-4">
        {version.lyrics_check ? (
          <LyricsCheckPanel versionId={version.id} check={version.lyrics_check} recordHref={myVocal ? `/create/project/${projectId}/sing?version=${version.derived_from}` : singHref}
            recordLabel={myVocal ? "Record another take" : undefined} onRecheck={recheck} onRegenerate={myVocal ? null : onRegenerate} rechecking={rechecking} />
        ) : hasLyrics && !myVoice ? (
          <button type="button" data-testid={`version-check-lyrics-${version.id}`} onClick={recheck} disabled={rechecking} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/55 hover:text-white disabled:opacity-40">
            {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ear className="h-3 w-3" />} Check lyrics
          </button>
        ) : null}
      </div>
      {version.song_id ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/create/song/${version.song_id}`} data-testid={`version-open-${version.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white">
            Master · Export <ArrowRight className="h-3 w-3" />
          </Link>
          <Link to={`/create/song/${version.song_id}/stems`} data-testid={`version-stems-${version.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-cyan-100 hover:bg-cyan-500/20">
            <Layers3 className="h-3 w-3" /> Stems
          </Link>
          <Link to={`/create/song/${version.song_id}/mix`} data-testid={`version-mix-${version.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-amber-100 hover:bg-amber-500/20">
            <SlidersVertical className="h-3 w-3" /> Mix Desk
          </Link>
          {singHref ? (
            <Link to={singHref} data-testid={`version-record-${version.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-rose-300/40 bg-rose-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-rose-100 hover:bg-rose-500/20">
              <Mic2 className="h-3 w-3" /> Sing it myself
            </Link>
          ) : null}
          <OpenInStudioButton projectId={projectId} version={version} lyrics={lyrics} source={source} />
          {replaceHref ? (
            <Link to={replaceHref} data-testid={`version-replace-${version.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-amber-100 hover:bg-amber-500/20">
              <Repeat2 className="h-3 w-3" /> {myVocal ? "Touch up vocal" : "Replace lead"}
            </Link>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
